/**
 * shadcn/ui `use-toast` hook — minimálna verzia pre R1.
 *
 * Toasty sa pridávajú do globálneho stavu cez `toast(...)` a `Toaster`
 * komponent (`@/components/ui/toaster`) ich renderuje. V plnej verzii
 * shadcn drží stav cez reducer; pre R1 nám stačí jednoduchý event bus.
 */

import * as React from "react";
import type { ToastProps } from "@/components/ui/toast";

type ToasterToast = ToastProps & {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
};

type State = {
  toasts: ToasterToast[];
};

const listeners: Array<(state: State) => void> = [];
let memoryState: State = { toasts: [] };

function dispatch(updater: (state: State) => State): void {
  memoryState = updater(memoryState);
  listeners.forEach((l) => l(memoryState));
}

let counter = 0;
function genId(): string {
  counter += 1;
  return String(counter);
}

const TOAST_LIMIT = 3;
const TOAST_REMOVE_DELAY = 4000;

export function toast(
  props: Omit<ToasterToast, "id">,
): { id: string; dismiss: () => void } {
  const id = genId();
  const newToast: ToasterToast = { ...props, id, open: true };
  dispatch((state) => ({
    toasts: [newToast, ...state.toasts].slice(0, TOAST_LIMIT),
  }));
  setTimeout(() => {
    dispatch((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  }, TOAST_REMOVE_DELAY);
  return {
    id,
    dismiss: () =>
      dispatch((state) => ({
        toasts: state.toasts.filter((t) => t.id !== id),
      })),
  };
}

export function useToast(): {
  toasts: ToasterToast[];
  toast: typeof toast;
} {
  const [state, setState] = React.useState<State>(memoryState);
  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const idx = listeners.indexOf(setState);
      if (idx > -1) listeners.splice(idx, 1);
    };
  }, []);
  return { toasts: state.toasts, toast };
}
